import React, { useEffect, useMemo, useState } from 'react';
import { Box, Collapse, Grid, IconButton, Link, ListItemIcon, Menu, MenuItem, Typography, useMediaQuery, useTheme } from "@mui/material";
import Card from "../../common/Card";
import Table, { Row } from "../../common/CustomTable";
import Header from "../../common/Header";
import Button from "../../common/Button";
import DifferenceIcon from '@mui/icons-material/Difference';
import {
  AddCircleOutlineOutlined, AppRegistrationOutlined, DeleteOutlineOutlined, ListAltOutlined, ModeOutlined,
  SearchOutlined, SummarizeOutlined, VisibilityOutlined, EditNote, ViewInArOutlined, ContentCopyOutlined,
  EditNoteOutlined,
  RemoveShoppingCartOutlined,
  PictureAsPdfOutlined,
  TrackChanges,
  CloudDownload,
  DownloadOutlined,
  KeyboardArrowDown,
  ExitToApp,
  NotificationsOutlined,
  Notifications,
  AssignmentTurnedInOutlined
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomTextField from '../../common/CustomTextField';
import LegacyToolBar from '../../common/LegacyToolBar';
import { useSelector } from 'react-redux';
import { ProductInfo } from '../../../redux/slice/product/productListSlice';
import { useAppDispatch } from '../../../redux/slice/redux-hooks';
import { PracticeInfo } from '../../../redux/slice/practiceSlice';
import { selectIsTokenAvailable, selectIsValueFoundInToken, selectTokenValues, setSelectedModule, useAuthSlice } from '../../../redux/slice/authSlice';
import { AUDIT_ENTITYS, CAN_ACCESS_APPLICATION, EXPORTABLE_ITEMS, IVR_STATUS, IVR_STATUS_LIST, ORDERTYPE, PRODUCT_CATEGORY, PRODUCT_CATEGORY_CODETABLE, USER_ROLES } from '../../../constants/applicationConstants';
import { formatDate, fullName, hideElement, sortNames } from '../../../utils/Utility';
import { createListSort } from '../../../utils/listSort';
import {
  SearchIVRListRequest, cancelIVRById, deleteIVRById, fetchIVRList, fetchProductListIncludingInactiveByUserRoleCreateIVRSlice,
  getPracticeListByUserRoleCreateIVRSlicenew,
  resetIVRList, useCreateIVRSlice
} from '../../../redux/slice/ivr/createIVRSlice';
import { RootState } from '../../../redux/store';
import OptionsPopup, { OptionPopupObjectProps } from '../../common/OptionsPopup';
import { fetchPracticeProductsData } from '../../../redux/slice/practice/practiceProductSlice';
import IVRPracticeSelectionPopup from './IVRPracticeSelectionPopup';
import CustomIconButton from '../../common/CustomIconButton';
import PDFViewer from '../../common/pdf/PDFViewer';
import { fetchDocumentById, useIVRDocumentSlice } from '../../../redux/slice/ivr/ivrDocumentSlice';
import CustomMultipleDropDown from '../../common/CustomMultipleDropDown';
import { CodeTable, fetchAllCodeTablesValues, getCodeTableValuesByTableName, useCodetablesSlice } from '../../../redux/slice/codetableSlice';
import { fetchManufacturersListSkipLoading, setSingleAlertObj, showHideLoadingDialog, SingleAlertInfo, useCommonReducer } from '../../../redux/slice/commonSlice';
import { getSearchCriteriaFromSessionStorage, saveSelectionForPage, setSearchCriteriaToSessionStorage } from '../../../utils/StorageUtils';
import CustomDateRange from '../../common/CustomDateRange';
import { DateObject } from 'react-multi-date-picker';
import InfoPopup from '../../common/InfoPopup';
import { useNavigation } from '../../../hooks/useNavigation';
import { useFilterPanelFocus } from "../../../hooks/useFilterPanelFocus";
import ExportToExcel from '../../common/ExportToExcel';
import MoreVertIcon from "@mui/icons-material/MoreVert";
import StyledMenu from '../../common/StyledMenu';
import WarningPopup, { WarningPopupObjectProps } from "../../common/WarningPopup";
import { acknowledgeNotification, fetchAlertEntityNumbers, fetchNotificationsByRefEntities, useCreateNotificationSlice } from '../../../redux/slice/inbox/createNotificationSlice';
import StatusInfoPopup from '../../common/StatusInfoPopup';
import { getPatient } from '../../../redux/slice/patient/patientSlice';
import dayjs from 'dayjs';
import { fetchManufacturerListByRole, useManufacturerListSlice } from '../../../redux/slice/manufacturer/ManufacturerSlice';
import { getProductConfigByProduct, ProductConfigRules } from '../../../service/ProductService';
import { PRODUCT_CONFIG_CODE, PRODUCT_CONFIG_TYPE, PRODUCT_CONFIG_VALUE } from '../../../constants/applicationConstants';
import CreateIVRModal from '../account/CreateIVRModal';
import { useModalStyles } from "../../common/useModalStyles";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
}

type Option = {
  value: string,
  label: string
};

export type IVRInfo = {
  practice: string;
  patient: string;
  product: string;
  provider: string;
  dateCreated: String;
  createdDate: string;
  dateOfService: JSX.Element;
  status: string;
  ivrStatus?: string | JSX.Element;
  locationId: number;
  productId: number;
  practiceId: number;
  ivrId: number;
  ivrNumber: string;
  ivrNumberLink?: string | JSX.Element; //CS-1406 - Integrated ivr number clickable to open details page
  patientId: number;
  patientDOB: String;
  practiceProductId: number;
  ivrDocumentId: number;
  rawIvrNumberLink?: string | number | null; // CS-1517 - For custom sorting
}

const ITEMS_PER_PAGE = 20;

const ivrListSort = createListSort(
  ["ivrNumber", "practiceName", "patientName", "productName", "manufacturerName", "providerName", "dateOfService", "verificationStatus", "createdDate", "auditTimestamp"] as const,
  {
    ivrNumberLink: "ivrNumber",
    practice: "practiceName",
    patient: "patientName",
    product: "productName",
    manufacturer: "manufacturerName",
    provider: "providerName",
    dateOfService: "dateOfService",
    ivrStatus: "verificationStatus",
    createdDate: "createdDate",
  },
  { auditTimestamp: "dateCreated" }
);

const getConfigValue = (configRules: ProductConfigRules[], configType: string, configCode: string): string | null => {
  const rule = configRules.find(r => r.configType === configType && r.configCode === configCode);
  return rule?.value ?? null;
};

const IVRList: React.FC = () => {

  const location = useLocation();
  const { navigate } = useNavigation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { primaryBtnSx, tokens: modalTokens } = useModalStyles();

  const { control, handleSubmit, reset, setValue, getValues } = useForm<SearchIVRListRequest>();

  const [showIVRSelectionPopup, setShowIVRSelectionPopup] = useState(false);
  const [ivrSelectionPractice, setIvrSelectionPractice] = useState<number | null>(null);
  const [showPDF, setShowPDF] = useState<{ isShow: boolean, productName: string, fileName: string }>({ isShow: false, productName: "", fileName: "" });
  const [deleteConfirm, setDeleteConfirm] = useState<{ message: string, show: boolean, row: any }>({ message: "", show: false, row: "" });
  const [cancelConfirm, setCancelConfirm] = useState<{ message: string, show: boolean, row: any }>({ message: "", show: false, row: "" });
  const [showInfoPopup, setShowInfoPopup] = useState<{ message: string, show: boolean }>({ message: "", show: false });
  const [showPHIWarning, setShowPHIWarning] = useState<WarningPopupObjectProps>({ show: false, message: '' });
  const [showCard, setShowCard] = useState(false);
  const {
    filterPanelRef,
    toggleFilterPanel,
    handleFilterPanelEntering,
    handleFilterPanelEntered,
    handleFilterPanelExited,
  } = useFilterPanelFocus(showCard, setShowCard);
  const [createIVROpen, setCreateIVROpen] = useState(false);
  const [showIvrCopyMessage, setShowIvrCopyMessage] = useState<{ message: string, show: boolean, row: any }>({ message: "", show: false, row: "" });
  const [showCreateIvrPopup, setCreateIvrPopup] = useState<{ message: string, show: boolean, title: string }>({ message: "", show: false, title: "" });

  const [isDateRangeChanged, setIsDateRangeChanged] = useState(false);
  const [isClearDateRange, setIsClearDateRange] = useState(false);

  const [searchCriteria, setSearchCriteria] = useState<SearchIVRListRequest>({
    practiceId: [],
    productId: [],
    mfgIds: [], //CS-1625
    productCategory: [],
    patientFirstName: "",
    patientLastName: "",
    verificationStatus: [],
    ivrNumber: "",
    fromDate: "",
    toDate: "",
    currentPage: 1,
    itemsPerPage: ITEMS_PER_PAGE,
  });

  const { userInfo } = useAuthSlice();
  const { IVRListInfo, practiceByUserRoleCreateIVRSlice, productsByUserRoleCreateIVRSlice } = useCreateIVRSlice();
  const { ivrDocumentInfo } = useIVRDocumentSlice();
  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } = useCodetablesSlice();
  const [productCategories, setProductCategories] = React.useState<CodeTable[]>([]);
  const [selectedRow, setSelectedRow] = useState<IVRInfo>();
  // const [patientData, setPatientData] = useState<{firstName: string, lastName: string, patientId: number}| null>(null);

  const canCreateDirectOrder = useSelector((state: RootState) => selectIsTokenAvailable("canCreateDirectOrder")(state));
  const canCreateIVR = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessIVRActions", IVR_STATUS.CREATE)(state));
  const canRespondIVR = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessIVRActions", IVR_STATUS.RESPOND)(state));
  const canEditIVR = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessIVRActions", IVR_STATUS.EDIT)(state));
  const canDeleteIVR = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessIVRActions", IVR_STATUS.DELETE)(state));
  const canViewIVR = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessIVRActions", IVR_STATUS.VIEW)(state));
  const canNoteIVR = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessIVRActions", IVR_STATUS.NOTE)(state));
  const canCopyIVR = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessIVRActions", IVR_STATUS.COPY)(state));
  const canDuplicateIVR = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessIVRActions", IVR_STATUS.DUPLICATE)(state));
  const canCancelIVR = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessIVRActions", IVR_STATUS.CANCEL)(state));
  const canAuditIVR = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessAuditAction", AUDIT_ENTITYS.IVRAUDIT)(state));
  const canAccessProviderProducts = useSelector((state: RootState) => selectTokenValues("canAccessProviderProducts")(state));
  const canExportIVRList = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessExportExcel", EXPORTABLE_ITEMS.IVR)(state));
  const accessibleNotifications = useSelector((state: RootState) => selectTokenValues("canAccessNotifications")(state));
  const canAccessNotifications = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessApplication", CAN_ACCESS_APPLICATION.NOTIFICATION_MANAGEMENT)(state));

  const [filteredProductsByUserRoleCreateIVRSlice, setFilteredProductsByUserRoleCreateIVRSlice] = useState<ProductInfo[]>();
  const [anchorElMenu, setAnchorElMenu] = useState<null | HTMLElement>(null);
  //CS-1071 -Start
  const [activeButtonId, setActiveButtonId] = useState<string | null>(null);
  const { NotificationInfoByRefEntity } = useCreateNotificationSlice();
  const [alertedOrderIds, setAlertedOrderIds] = useState<number[]>([]);
  const [notificationData, setNotificationData] = useState<any[]>([]);
  const [totalNotificationIds, setTotalNotificationIds] = useState([]);
  const [statusAnchorEl, setStatusAnchorEl] = useState<HTMLElement | null>(null);
  const [isAlertOnly, setIsAlertOnly] = useState<boolean>(false);
  const [loadingComplete, setLoadingComplete] = useState<Boolean>(false);
  const [fetchPracticeAndProductsCompleted, setFetchPracticeAndProductsCompleted] = useState<Boolean>(false);
  const [fetchNotificationCountCompleted, setFetchNotificationCountCompleted] = useState<Boolean>(false);
  const [fetchNotificationsCompleted, setFetchNotificationsCompleted] = useState<Boolean>(false);
  //CS-1625
  // const { manufacturerInfoList } = useCommonReducer();
  const { manufacturerInfoListByUserRole } = useManufacturerListSlice();

  const listBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
  const listPanelBg = isDark ? "#141720" : "rgba(255,255,255,0.82)";
  const listHoverBg = isDark ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)";

  // Add this helper function near the top of the component (after getConfigValue)
  const getStatusColor = (status: string): string => {
    switch (status) {
      case IVR_STATUS.VERIFIED: return "#22c55e";  // green
      case IVR_STATUS.SUBMITTED: return "#3b82f6";  // blue
      case IVR_STATUS.DRAFTED: return "#94a3b8";  // slate
      case IVR_STATUS.DENIED: return "#ef4444";  // red
      case IVR_STATUS.CANCELED: return "#f97316";  // orange
      case IVR_STATUS.NEEDS_MORE_INFO: return "#f59e0b"; // amber
      case IVR_STATUS.PRE_AUTH: return "#a855f7";  // purple
      default: return modalTokens.textPrimary;
    }
  };

  const listCardSx = {
    borderRadius: "14px",
    border: `1px solid ${listBorderColor}`,
    backgroundColor: listPanelBg,
    backgroundImage: "none",
    backdropFilter: "blur(8px)",
    boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.28)" : "0 12px 28px rgba(15,23,42,0.08)",
  };

  const filterPanelSx = {
    ...listCardSx,
    p: { xs: "14px !important", md: "18px !important" },
    position: "relative",
    zIndex: 20,
    overflow: "visible",
    "& .MuiAutocomplete-root, & .MuiFormControl-root": { width: "100%" },
    "& .MuiTextField-root": { height: "42px", minHeight: "42px !important" },
    "& .MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiAutocomplete-inputRoot, & .MuiAutocomplete-root .MuiOutlinedInput-root": {
      minHeight: "42px !important",
      height: "42px !important",
      borderRadius: "10px !important",
      backgroundColor: `${modalTokens.inputBg} !important`,
      color: modalTokens.textPrimary,
      fontSize: "0.88rem",
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
      alignItems: "center",
      boxSizing: "border-box",
    },
    "& .MuiAutocomplete-root .MuiOutlinedInput-root": {
      padding: "0 38px 0 12px !important",
    },
    "& .MuiAutocomplete-root .MuiInputBase-input, & .MuiAutocomplete-input, & .MuiOutlinedInput-input": {
      paddingTop: "8px !important",
      paddingBottom: "8px !important",
      height: "24px !important",
      lineHeight: "24px !important",
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-input": {
      paddingLeft: "12px !important",
      paddingRight: "12px !important",
    },
    "& .MuiAutocomplete-endAdornment": {
      top: "50% !important",
      transform: "translateY(-50%)",
      right: "8px !important",
    },
    "& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root fieldset": {
      borderColor: `${modalTokens.inputBorder} !important`,
    },
    "& .MuiOutlinedInput-root:hover fieldset, & .MuiOutlinedInput-root.Mui-focused fieldset": {
      borderColor: `${modalTokens.accent} !important`,
      borderWidth: "1px !important",
    },
    "& .MuiOutlinedInput-root.Mui-focused": {
      boxShadow: isDark ? "none" : "0 0 0 3px rgba(20,184,166,0.12)",
    },
    "& .MuiInputBase-input": { color: modalTokens.textPrimary, fontSize: "0.88rem" },
    "& input::selection": {
      color: modalTokens.textPrimary,
      backgroundColor: isDark ? "rgba(20,184,166,0.28)" : "rgba(20,184,166,0.2)",
    },
    "& .MuiOutlinedInput-root:has(input:-webkit-autofill)": {
      backgroundColor: "#376c9f !important",
      "& fieldset": { borderColor: "#7fb1df !important" },
    },
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
      WebkitTextFillColor: "#ffffff",
      caretColor: "#ffffff",
      WebkitBoxShadow: "0 0 0 100px #376c9f inset",
      borderRadius: "10px",
      transition: "background-color 5000s ease-in-out 0s",
    },
    "& .MuiInputLabel-root": { color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)", fontSize: "0.88rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: `${modalTokens.accent} !important` },
    "& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": { transform: "translate(14px, 9px) scale(1)" },
    "& .MuiChip-root": {
      borderRadius: "999px",
      backgroundColor: isDark ? "rgba(20,184,166,0.16)" : "rgba(20,184,166,0.1)",
      color: modalTokens.accent,
      border: `1px solid ${isDark ? "rgba(20,184,166,0.26)" : "rgba(20,184,166,0.18)"}`,
      fontWeight: 600,
    },
    "& .MuiSvgIcon-root": { color: modalTokens.textSecondary },
    "& .rmdp-container": { width: "100%", height: "42px", overflow: "visible", zIndex: 30 },
    "& .rmdp-wrapper, & .rmdp-calendar, & .rmdp-day-picker": {
      height: "auto !important",
      minHeight: "unset !important",
      overflow: "visible",
    },
    "& .rmdp-wrapper": { zIndex: "1600 !important", position: "absolute" },
  };

  const filterFieldLabelSx = {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: modalTokens.textSecondary,
    mb: 0.7,
    height: "16px",
    lineHeight: "16px",
  };

  const primaryActionBtnSx = {
    ...primaryBtnSx,
    color: "#fff",
    "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
    "& .MuiButton-startIcon": { color: "#fff" },
  };

  const outlineBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#00B8D9",
    borderColor: "rgba(0,184,217,0.55)",
    backgroundColor: isDark ? "rgba(0,184,217,0.08)" : "rgba(0,184,217,0.04)",
    whiteSpace: "nowrap",
    "& .MuiSvgIcon-root": {
      color: "#00B8D9",
    },
    "&:hover": {
      color: "#fff",
      borderColor: "#00B8D9",
      backgroundColor: "#00B8D9",
      boxShadow: "0 6px 22px rgba(0,184,217,0.28)",
      "& .MuiSvgIcon-root": {
        color: "#fff",
      },
    },
  };

  const exportBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#14B8A6",
    borderColor: "rgba(20,184,166,0.55)",
    backgroundColor: isDark ? "rgba(20,184,166,0.08)" : "rgba(20,184,166,0.04)",
    whiteSpace: "nowrap",
    "& .MuiSvgIcon-root": {
      color: "#14B8A6",
    },
    "&:hover": {
      color: "#fff",
      borderColor: "#14B8A6",
      backgroundColor: "#14B8A6",
      boxShadow: "0 6px 22px rgba(20,184,166,0.28)",
      "& .MuiSvgIcon-root": {
        color: "#fff",
      },
    },
  };

  const exportMenuItemBtnSx = {
    justifyContent: "flex-start",
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "8px",
    py: 1,
    px: 1.4,
    color: "#2dd4bf",
    borderColor: "rgba(45,212,191,0.35)",
    backgroundColor: isDark ? "rgba(20,184,166,0.13)" : "rgba(20,184,166,0.08)",
    boxShadow: "none",
    "& .MuiButton-startIcon": { mr: 1, "& svg": { fontSize: "18px" } },
    "&:hover": {
      color: "#fff",
      borderColor: "#2f9b8f",
      backgroundColor: "#2f9b8f",
      boxShadow: "0 10px 24px rgba(20,184,166,0.28)",
    },
    "&.Mui-disabled": {
      color: isDark ? "rgba(148,163,184,0.55)" : "rgba(100,116,139,0.7)",
      borderColor: isDark ? "rgba(148,163,184,0.18)" : "rgba(100,116,139,0.24)",
      backgroundColor: isDark ? "rgba(148,163,184,0.06)" : "rgba(100,116,139,0.06)",
    },
  };

  const ivrTableSx = {
    "& .MuiTableContainer-root": {
      borderRadius: "12px",
      border: "none",
      backgroundColor: "transparent",
    },
    "& table": {
      
      borderCollapse: "collapse",
      borderSpacing: 0,
    },
    "& .MuiTableCell-root": {
      borderBottom: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
      py: 1.15,
      fontSize: "0.84rem",
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      height: "42px",
    },
    "& .MuiTableHead-root .MuiTypography-root": {
      color: "#00B8D9 !important",
      fontSize: "0.78rem",
      fontWeight: 800,
      letterSpacing: "0.02em",
    },
    "& .MuiTableBody-root .MuiTableRow-root": {
      transition: "background-color 0.18s ease, box-shadow 0.18s ease",
    },
    "& .MuiTableBody-root .MuiTableRow-root:hover": {
      backgroundColor: `${listHoverBg} !important`,
    },
    "& .MuiTableBody-root .MuiTableCell-root, & .MuiTableBody-root .MuiTypography-root": {
      color: modalTokens.textPrimary,
      fontSize: "0.84rem",
    },
    "& .MuiTableBody-root .ivr-status-text": {
      color: "inherit",  // lets the inline sx color take effect
    },
    "& .MuiTableBody-root .MuiTableCell-root:first-of-type .MuiLink-root": {
      color: "#00B8D9 !important",
      fontWeight: 600,
      textDecorationColor: "rgba(0,184,217,0.45)",
    },
    "& .MuiTableBody-root .MuiTableCell-root:first-of-type .MuiLink-root:hover": {
      color: "#22d3ee !important",
      textDecorationColor: "#22d3ee",
    },
    "& .MuiIconButton-root": {
      color: isDark ? "#94a3b8" : "#475569",
      borderRadius: "8px",
      "&:hover": {
        color: modalTokens.accent,
        backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)",
      },
    },
    "& .MuiPaginationItem-root": {
      borderColor: listBorderColor,
      color: modalTokens.textSecondary,
      borderRadius: "8px",
      "&.Mui-selected": {
        color: "#fff",
        backgroundColor: modalTokens.accent,
        borderColor: modalTokens.accent,
      },
    },
  };

  useEffect(() => {
    const saved = sessionStorage.getItem("activeActionButtonId");
    if (saved) {
      setActiveButtonId(saved);
    }
  }, []);
  //CS-1071 -End

  const handleStatusClick = (event: React.MouseEvent<HTMLElement>, details: any) => {
    const notificationsForIVR = groupedNotifications.get(details.ivrId) || [];

    const popupData = notificationsForIVR.map((notif) => ({
      notificationId: notif.notificationId,
      orderNumber: details.IVRNumber,
      description: notif.notificationMsg,
      dateTime: notif.auditTimestamp,
    }));

    setNotificationData(popupData);
    setStatusAnchorEl(event.currentTarget);
  };

  const fetchNotificationsForEntities = async () => {
    const ivrIdList = IVRListInfo?.content?.map(ivr => ivr.ivrId);
    const data = {
      notificationTypes: accessibleNotifications ? accessibleNotifications : [],
      refEntityIds: ivrIdList ? ivrIdList : [],
      refEntity: "IVR"
    };

    if (canAccessNotifications && ivrIdList) {
      try {
        await dispatch(
          fetchNotificationsByRefEntities({ data, skipLoader: true })
        ).unwrap();

        setFetchNotificationsCompleted(true);
      } catch (err) {
        console.log("Failed to fetch notifications by ref entities", err);
        setFetchNotificationsCompleted(true);
      }
      setFetchNotificationsCompleted(true)
    }
  }

  const handleAcknowledge = async (item: any) => {
    try {
      const notificationData = {
        notificationId: item.notificationId,
        comment: ""
      }
      const response = await dispatch(acknowledgeNotification(notificationData)).unwrap();
      if (response.message === "SUCCESS") {
        fetchNotificationsForEntities();
        handleFetchAlerts();
      }
      return response;
    } catch (err) {
      console.error("Failed to acknowledge notification:", err);
      return [];
    }
  }

  const handleFetchAlerts = async () => {
    const data = {
      notificationTypes: accessibleNotifications ? accessibleNotifications : [],
      entity: "IVR"
    }
    if (canAccessNotifications) {
      const response = await dispatch(fetchAlertEntityNumbers({ data: data, skipLoader: true })).unwrap();
      setTotalNotificationIds(response?.data?.totalNotificationIds)
      setFetchNotificationCountCompleted(true)
    }
  }

  useEffect(() => {
    if (userInfo.notificationEnable && canAccessNotifications &&
      accessibleNotifications && accessibleNotifications.length > 0) {
      handleFetchAlerts();
    }
  }, [accessibleNotifications])

  useEffect(() => {
    if (userInfo.notificationEnable) {
      fetchNotificationsForEntities();
    }
  }, [IVRListInfo])

  //CS-1625
  useEffect(() => {
    const fechManufacturers = async () => {
      const res = await dispatch(fetchManufacturerListByRole({ skipLoader: true }));
      // const res = await dispatch(fetchManufacturersListSkipLoading({skipLoader: true}));
    }
    fechManufacturers();
  }, []);

  // CS-1289 START
  useEffect(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      const sortedProductCategoryList = [
        ...getCodeTableValuesByTableName(allCodeTablesValuesMap, PRODUCT_CATEGORY_CODETABLE),
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
      setProductCategories(sortedProductCategoryList);
    }
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);
  // CS-1289 END

  const handleNotification = () => {
    if (!isAlertOnly) {
      setValue("fromDate", "");
      setValue("toDate", "");
      setValue("dateRange", "");
      setIsClearDateRange(true);
      setIsDateRangeChanged(false);

      if (location.state != null && location.state.ivrListSearchCriteria != null &&
        location.state.ivrListSearchCriteria.ivrIds && location.state.ivrListSearchCriteria.ivrIds.length > 0) {
        location.state.ivrListSearchCriteria = {
          ...location.state.ivrListSearchCriteria,
          ivrIds: []
        }
      }
      setIsAlertOnly(true)
      fetchInitialLists();

    } else {
      if (location.state != null && location.state.ivrListSearchCriteria != null &&
        location.state.ivrListSearchCriteria.ivrIds && location.state.ivrListSearchCriteria.ivrIds.length > 0) {
        location.state.ivrListSearchCriteria = {
          ...location.state.ivrListSearchCriteria,
          ivrIds: []
        }
      }
      setIsAlertOnly(false)
      fetchInitialLists();
    }

  }

  const groupedNotifications = useMemo(() => {
    const map = new Map<number, any[]>();
    NotificationInfoByRefEntity?.forEach((notif) => {
      const orderId = notif.entityId;
      if (!map.has(orderId)) {
        map.set(orderId, []);
      }
      map.get(orderId)?.push(notif);
    });
    return map;
  }, [NotificationInfoByRefEntity]);


  useEffect(() => {
    const alertedOrderIds = NotificationInfoByRefEntity?.map(notification => notification.entityId);
    if (alertedOrderIds) {
      setAlertedOrderIds(alertedOrderIds);
    }

  }, [NotificationInfoByRefEntity])


  useEffect(() => {
    const accessibleProductIds = canAccessProviderProducts[0] ? canAccessProviderProducts[0].split(',') : [];
    if (canAccessProviderProducts) {
      if (accessibleProductIds && accessibleProductIds.length > 0) {
        const filteredProducts = productsByUserRoleCreateIVRSlice.filter((product) =>
          accessibleProductIds.includes(product.productId.toString())
        );
        setFilteredProductsByUserRoleCreateIVRSlice(filteredProducts);
      } else {
        setFilteredProductsByUserRoleCreateIVRSlice([])
      }
    } else {
      setFilteredProductsByUserRoleCreateIVRSlice(productsByUserRoleCreateIVRSlice);
    }
  }, [productsByUserRoleCreateIVRSlice, canAccessProviderProducts]);


  const statusOptions = useMemo(() => {
    let statuses: CodeTable[] = [];
    if (allCodeTablesValuesMap) {
      statuses = getCodeTableValuesByTableName(allCodeTablesValuesMap, IVR_STATUS_LIST);
    }
    if (!statuses || statuses.length === 0) {
      dispatch(fetchAllCodeTablesValues());
    }
    let options: { label: string, value: any }[] = [];
    statuses?.map((option) => {
      options.push({ label: option.displayValue, value: option.displayValue });
    });
    return options;
  }, [allCodeTablesValuesMap]);

  const updateCriteria = (key: string, value: number[]) => {
    const sessionName = CAN_ACCESS_APPLICATION.IVR_MANAGEMENT + "SearchCriteria" + userInfo.userId;
    //@ts-ignore
    setValue(key, value);
    let criteria = getSearchCriteriaFromSessionStorage(sessionName);
    const updated = { ...criteria, [key]: value };
    setSearchCriteriaToSessionStorage(sessionName, updated);
    setSearchCriteria(updated);
  };

  const practiceOptions = useMemo(() => {
    let options: Option[] = [];
    if (practiceByUserRoleCreateIVRSlice && practiceByUserRoleCreateIVRSlice.length > 0) {
      if (practiceByUserRoleCreateIVRSlice.length === 1) {
        const practiceVal = [Number(practiceByUserRoleCreateIVRSlice[0]?.practiceId)];
        updateCriteria('practiceId', practiceVal);
      }
      const sortedPracticeInfo: PracticeInfo[] = sortNames(practiceByUserRoleCreateIVRSlice, 'practiceName');
      sortedPracticeInfo?.map(practice => {
        options.push({ value: practice.practiceId, label: practice.practiceName });
      });
    };
    return options;
  }, [practiceByUserRoleCreateIVRSlice]);

  //CS-1625
  // const manufacturesList = useMemo(() => {
  //     let options: { label: string; value: any }[] = [];

  //     if ((filteredProductsByUserRoleCreateIVRSlice?.length ?? 0) > 0 && manufacturerInfoList?.length > 0) {
  //       const uniqueManufacturerIds = (filteredProductsByUserRoleCreateIVRSlice ?? [])
  //         .map((product) => product.manufacturerId)
  //         .filter((id, index, self) => id !== null && self.indexOf(id) === index);

  //       const filteredManufacturers = manufacturerInfoList.filter((manufacturer) =>
  //         uniqueManufacturerIds.includes(manufacturer.manufacturerId) 
  //       );

  //       if (manufacturerInfoList.length === 1) {
  //         const mfgVal = [Number(manufacturerInfoList[0]?.manufacturerId)];
  //         updateCriteria('manufacturerIds', mfgVal);
  //       }
  //       //CS-1564 - Show INACTIVE with manufacturers label
  //        filteredManufacturers.forEach((item) => {
  //         options.push({
  //           label: item.manufacturerName,
  //           value: item.manufacturerId,
  //         });
  //       });
  //       // filteredManufacturers.forEach((item) => {
  //       //   const isInactive =
  //       //     item?.inactiveDate &&
  //       //     dayjs(item.inactiveDate).isBefore(dayjs(), "day");

  //       //   options.push({
  //       //     label: isInactive
  //       //       ? `${item.manufacturerName} (INACTIVE)`
  //       //       : item.manufacturerName,
  //       //     value: item.manufacturerId,
  //       //   });
  //       // });

  //     }

  //     return options;
  //   }, [filteredProductsByUserRoleCreateIVRSlice, manufacturerInfoList]);  
  const manufacturesList = useMemo(() => {
    let options: { label: string; value: any }[] = [];

    if (manufacturerInfoListByUserRole && manufacturerInfoListByUserRole.length > 0) {
      if (manufacturerInfoListByUserRole.length === 1) {
        const mfgVal = [Number(manufacturerInfoListByUserRole[0]?.manufacturerId)];
        updateCriteria('manufacturerIds', mfgVal);
      }
      manufacturerInfoListByUserRole.forEach((item) => {
        options.push({
          label: item.manufacturerName,
          value: item.manufacturerId,
        });
      });
    }
    return options;
  }, [manufacturerInfoListByUserRole]);
  const productOptions = useMemo(() => {
    let options: Option[] = [];
    if (filteredProductsByUserRoleCreateIVRSlice && filteredProductsByUserRoleCreateIVRSlice.length > 0) {
      if (filteredProductsByUserRoleCreateIVRSlice.length === 1) {
        const productVal = [Number(filteredProductsByUserRoleCreateIVRSlice[0]?.productId)]
        updateCriteria('productId', productVal);
      }
      const sortedProductList: ProductInfo[] = sortNames(filteredProductsByUserRoleCreateIVRSlice, 'productName');
      sortedProductList?.map((option) => {
        options.push({ label: option.productName, value: option.productId })
      });
    };
    return options;
  }, [filteredProductsByUserRoleCreateIVRSlice]);

  // CS-1289 START
  const productCategoryOptions = useMemo(() => {
    return productCategories.map(item => ({
      label: item.displayValue,
      value: item.keyName
    }));
  }, [productCategories]);
  // CS-1289 END

  const formattedIVRInfo: IVRInfo[] = useMemo(() => {
    let ivrs: IVRInfo[] = [];
    if (IVRListInfo) {
      ivrs = IVRListInfo.content.map((ivr: any) => ({
        practice: ivr.practiceName,
        patient: fullName(ivr.title, ivr.patientFirstName, ivr.patientMiddleName, ivr.patientLastName),
        // productCategory: ivr.productCategory, // CS-1326 
        //CS-1406 - Integrated ivr number clickable to open details page
        ivrNumberLink: ivr?.ivrId ? (
          <Link
            component="button"
            underline="hover"
            variant="body2"
            onClick={(e) => {
              e.stopPropagation(); handleActionButtonClick(`ivrListActionButton-${ivr.ivrId}`, () => navigate("/ivr_details", { // CS-1555
                state: {
                  ivrInfo: { ...ivr, patient: fullName(ivr.title, ivr.patientFirstName, ivr.patientMiddleName, ivr.patientLastName), dateCreated: ivr.auditTimestamp },
                }
              }))
            }}
            color={'#00B8D9'}
          >
            {ivr?.ivrNumber}
          </Link>
        ) : (
          ivr?.ivrNumber
        ),
        product: ivr.productName,
        provider: ivr.providerName,
        dateCreated: ivr.auditTimestamp,
        createdDate: ivr.createdDate,
        dateOfService: ivr.dateOfService,
        status: ivr.verificationStatus,
        ivrStatus: userInfo.notificationEnable ? (
          <Box display="flex" alignItems="center" gap={1}>
            {alertedOrderIds.includes(ivr.ivrId) && (
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusClick(e, ivr)
                }}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: "#2196f3",
                  display: "inline-block",
                  cursor: "pointer",
                }}
              />
            )}
            <Typography
              variant="body2"
              className="ivr-status-text"
              sx={{ display: "inline-block", color: getStatusColor(ivr.verificationStatus), fontWeight: 600 }}
            >
              {ivr.verificationStatus}
            </Typography>
          </Box>
        ) : (
          <Typography
            variant="body2"
            sx={{ display: "inline-block" }}
          >
            {ivr.verificationStatus}
          </Typography>
        ),
        locationId: ivr.locationId,
        productId: ivr.productId,
        practiceId: ivr.practiceId,
        ivrId: ivr.ivrId,
        ivrNumber: ivr.ivrNumber,
        patientId: ivr.patientId,
        patientDOB: ivr.patientDOB,
        practiceProductId: ivr.practiceProductId,
        rawIvrNumberLink: ivr.ivrNumber, // CS-1517
        ivrDocumentId: ivr.ivrDocumentId,
        manufacturer: ivr.manufacturerName // CS-1625
      }));
    };
    return ivrs;
  }, [IVRListInfo, alertedOrderIds]);

  //Mount
  useEffect(() => {
    fetchInitialLists();
    const ivrDocId = location.state?.ivrDocId;
    const ivrNumber = location.state?.ivrNumber;
    // const patientDetails = location?.state?.patientDetails;

    if (ivrNumber) {
      setValue("ivrNumber", ivrNumber);
      setShowCard(true);

      const autoSearchCriteria: SearchIVRListRequest = {
        practiceId: [],
        mfgIds: [],//CS-1625
        productId: [],
        productCategory: [],
        patientFirstName: "",
        patientLastName: "",
        verificationStatus: [],
        ivrNumber: ivrNumber,
        fromDate: "",
        toDate: "",
        currentPage: 1,
        itemsPerPage: ITEMS_PER_PAGE,
        alertIVRs: isAlertOnly ? true : false
      };

      setSearchCriteria(autoSearchCriteria);
      searchIVRsList(autoSearchCriteria);

    }

    if (ivrDocId) {
      previewDoc(ivrDocId)
    }

    let criteria = getSearchCriteriaFromSessionStorage(CAN_ACCESS_APPLICATION.IVR_MANAGEMENT + "SearchCriteria" + userInfo.userId);
    if (criteria) {
      return;
    }


    // if(patientDetails) {
    //   setValue('patientFirstName', patientDetails.firstName);
    //   setValue('patientLastName', patientDetails.lastName);

    //   setPatientData({
    //     firstName: patientDetails.firstName,
    //     lastName: patientDetails.lastName,
    //     patientId: patientDetails.patientId
    //   });
    // } 
  }, []);

  const previewDoc = async (ivrDocId: string) => {
    const actionResult = await dispatch(fetchDocumentById(parseInt(ivrDocId)));
    const documentData = actionResult.payload;

    if (documentData && documentData.content) {
      setShowPDF({ isShow: true, productName: ivrDocId + ` - IVR`, fileName: ivrDocId });
    };
  }

  const fetchInitialLists = async () => {
    dispatch(showHideLoadingDialog(true));
    //Don't remove below unused variables
    //  const responsePractices = await dispatch((getPracticeListByUserRoleCreateIVRSlice(false)));
    const responsePractices = await dispatch((getPracticeListByUserRoleCreateIVRSlicenew({ isActivePracticeOnly: true, skipLoader: true })));
    const responseProducts = await dispatch(fetchProductListIncludingInactiveByUserRoleCreateIVRSlice({ includeProductPrice: false, executedOnly: true, skipLoader: true }));
    if (responsePractices && responseProducts) {
      setFetchPracticeAndProductsCompleted(true);
    }
    dispatch(showHideLoadingDialog(false));
  }

  //Unmount
  useEffect(() => () => {
    dispatch(resetIVRList());
  }, []);

  useEffect(() => {
    //1. When login another user pagination is remain in the history and can redirect that page
    let criteria = getSearchCriteriaFromSessionStorage(CAN_ACCESS_APPLICATION.IVR_MANAGEMENT + "SearchCriteria" + userInfo.userId);
    if ((location.state && location.state.ivrListSearchCriteria) || (location.state && location.state.withSearchHistory && criteria)) {
      if (location.state && location.state.withSearchHistory) {
        //Below navigate line call back to this useEffect - This for refreshing issue when navigate with state.withSearchHistory=true
        navigate("/ivr_list", { state: { ivrListSearchCriteria: criteria } });
        return;
      }
      let searchCriteria: SearchIVRListRequest = {
        productId: [],
        mfgIds: [], //CS-1625
        practiceId: [],
        productCategory: [],
        patientFirstName: getValues('patientFirstName') ? getValues('patientFirstName') : '',
        patientLastName: getValues('patientLastName') ? getValues('patientLastName') : '',
        verificationStatus: [],
        ivrNumber: '',
        fromDate: "",
        toDate: "",
        currentPage: 0,
        itemsPerPage: 0,
        alertIVRs: isAlertOnly ? true : false
      };
      if (location.state && location.state.ivrListSearchCriteria) {
        location.state.ivrListSearchCriteria.alertIVRs = isAlertOnly ? true : false;
        searchCriteria = location.state.ivrListSearchCriteria;
      } else {
        if (criteria) {
          criteria.alertIVRs = isAlertOnly ? true : false;
        }
        searchCriteria = criteria ? criteria : searchCriteria;
      }
      setSearchCriteria(searchCriteria);
      setValuesToCriteriaFields(searchCriteria);

      searchIVRsList(searchCriteria);
    } else {
      const dateRangeValue = getValues("dateRange");
      let fromDateRange = "";
      let toDateRange = "";

      if (dateRangeValue) {
        const dates = dateRangeValue.split(" - ");
        fromDateRange = dates[0] || "";
        toDateRange = dates[1] || "";
      }

      const initialSearchCriteria = {
        ...searchCriteria,
        alertIVRs: isAlertOnly ? true : false,
        fromDate: fromDateRange || "",
        toDate: toDateRange || "",
        patientFirstName: getValues('patientFirstName') ? getValues('patientFirstName') : '',
        patientLastName: getValues('patientLastName') ? getValues('patientLastName') : '',
        ivrNumber: getValues("ivrNumber") ? getValues("ivrNumber") : '',

      };
      setSearchCriteria(initialSearchCriteria);
      if (fromDateRange || toDateRange) {
        searchIVRsList(initialSearchCriteria);
        setIsDateRangeChanged(false);
      } else {
        loadInitialIVRList(1);
      }
    }
  }, [location.state, isAlertOnly]);

  const setValuesToCriteriaFields = (searchCriteria: SearchIVRListRequest) => {
    setValue("practiceId", searchCriteria.practiceId);
    setValue("productId", searchCriteria.productId);
    setValue("patientFirstName", searchCriteria.patientFirstName);
    setValue("patientLastName", searchCriteria.patientLastName);
    setValue("verificationStatus", searchCriteria.verificationStatus);
    setValue("productCategory", searchCriteria.productCategory);
    setValue("ivrNumber", searchCriteria.ivrNumber);
    setValue("mfgIds", searchCriteria.mfgIds); //CS-1625
    if (searchCriteria.fromDate && searchCriteria.toDate) {
      const fromDate = searchCriteria.fromDate || "";
      const toDate = searchCriteria.toDate || "";
      setValue(
        "dateRange",
        fromDate && toDate ? `${fromDate} - ${toDate}` : ""
      );
    } else if (searchCriteria.fromDate) {
      const fromDate = searchCriteria.fromDate || "";
      setValue(
        "dateRange",
        fromDate ? `${fromDate}` : ""
      );
    }
  }

  const loadInitialIVRList = (pNumber: number = 1) => {
    const data: SearchIVRListRequest = {
      practiceId: [],
      mfgIds: [],//CS-1625
      productId: [],
      productCategory: [],
      patientFirstName: getValues('patientFirstName') ? getValues('patientFirstName') : '',
      patientLastName: getValues('patientLastName') ? getValues('patientLastName') : '',
      verificationStatus: [],
      ivrNumber: "",
      fromDate: "",
      toDate: "",
      currentPage: pNumber,
      itemsPerPage: ITEMS_PER_PAGE,
      alertIVRs: isAlertOnly ? true : false
    };
    setSearchCriteria(data);
    setValuesToCriteriaFields(data);

    searchIVRsList(data);
  }

  const searchIVRsList = (searchCriteria: SearchIVRListRequest) => {

    let dataToSearch = ivrListSort.normalize({
      ...searchCriteria
    });
    if (!dataToSearch.verificationStatus || dataToSearch.verificationStatus.length === 0) {
      dataToSearch.verificationStatus = statusOptions && statusOptions.length > 0 ? statusOptions?.map(status => status.value) : [];
    }

    setSearchCriteriaToSessionStorage(CAN_ACCESS_APPLICATION.IVR_MANAGEMENT + "SearchCriteria" + userInfo.userId, dataToSearch);

    if (userInfo.userRole !== USER_ROLES.ROLE_PRACTICE_USER_PRU) {
      dispatch(fetchIVRList({ data: dataToSearch, skipLoader: false }))
    }
  }

  const onSubmit = (data: SearchIVRListRequest) => {
    if (!isDateRangeChanged) {
      setValue("fromDate", "");
      setValue("toDate", "");
      setValue("dateRange", "");
      setIsClearDateRange(true);
    }
    const data2: SearchIVRListRequest = ivrListSort.normalize({
      practiceId: data.practiceId,
      productId: data.productId,
      productCategory: data.productCategory,
      patientFirstName: data.patientFirstName,
      patientLastName: data.patientLastName,
      verificationStatus: data.verificationStatus,
      ivrNumber: data.ivrNumber,
      fromDate: isDateRangeChanged ? data.fromDate : "",
      toDate: isDateRangeChanged ? data.toDate : "",
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE,
      mfgIds: data.mfgIds,
      sortBy: searchCriteria.sortBy,
      sortOrder: searchCriteria.sortOrder,
    });
    setSearchCriteria(data2);

    //Below navigate line call to useEffect[location.state, productsByUserRoleCreateIVRSlice] and search
    navigate("/ivr_list", { state: { ivrListSearchCriteria: data2 } });
  }

  const handlePagination = (pageNumber: number) => {
    const data = {
      ...searchCriteria,
      currentPage: pageNumber + 1
    }
    setSearchCriteria(data);

    //Below navigate line call to useEffect[location.state, productsByUserRoleCreateIVRSlice] and search
    navigate("/ivr_list", { state: { ivrListSearchCriteria: data } });
  }

  const handleTableSort = (columnId: string, order: "asc" | "desc") => {
    const data = ivrListSort.withSort({
      ...searchCriteria,
      itemsPerPage: searchCriteria.itemsPerPage || ITEMS_PER_PAGE,
    }, columnId, order);
    if (!data) {
      return;
    }
    setSearchCriteria(data);
    navigate("/ivr_list", { state: { ivrListSearchCriteria: data } });
  }

  const confirmDeleteIVR = async () => {
    const response = await dispatch(deleteIVRById(deleteConfirm.row));
    if (response.payload.message === "SUCCESS") {
      const alertObj: SingleAlertInfo = {
        message: `IVR ${selectedRow?.ivrNumber} has been deleted successfully.`,
        alertType: "success",
      };
      dispatch(setSingleAlertObj(alertObj));
      searchIVRsList(searchCriteria);
    }
    setDeleteConfirm({ message: "", show: false, row: "" });
  }

  const confirmCancelIVR = async () => {
    const response = await dispatch(cancelIVRById(cancelConfirm.row));
    if (response.payload.message === "SUCCESS") {
      const alertObj: SingleAlertInfo = {
        message: `IVR  ${selectedRow?.ivrNumber} has been cancelled successfully.`,
        alertType: "success",
      };
      dispatch(setSingleAlertObj(alertObj));
      searchIVRsList(searchCriteria);
    }
    setCancelConfirm({ message: "", show: false, row: "" });
  }

  const cancelDeleteIVR = () => {
    setDeleteConfirm({ message: "", show: false, row: "" });
  }

  const cancelCancelIVR = () => {
    setCancelConfirm({ message: "", show: false, row: "" });
  }

  const handlePreviewAction = async (row: IVRInfo) => {
    const actionResult = await dispatch(fetchDocumentById(row.ivrDocumentId));
    const documentData = actionResult.payload;

    if (documentData && documentData.content) {
      setShowPDF({ isShow: true, productName: row.product + ` - IVR`, fileName: row.ivrNumber });
    };
  }

  const handleCreateOrder = async (row: IVRInfo) => {
    const selectedIVR = IVRListInfo?.content?.find(ivr => ivr.ivrId === row.ivrId);
    if (selectedIVR) {
      dispatch(setSelectedModule("Order"));

      let productConfigRules: ProductConfigRules[] = [];
      try {
        const response = await getProductConfigByProduct(selectedIVR.productId);
        productConfigRules = response?.data?.data ?? [];
      } catch (error) {
        console.error("Failed to fetch product config rules:", error);
      }

      const orderWorkflowType = getConfigValue(productConfigRules, PRODUCT_CONFIG_TYPE.ORDER_WORKFLOW, PRODUCT_CONFIG_CODE.TYPE);
      const isAncillaryWorkflow = orderWorkflowType === PRODUCT_CONFIG_VALUE.COLLAGEN;
      const isAncillaryByCategory = selectedIVR.productCategory === PRODUCT_CATEGORY.CONSERVATIVE;
      const isAncillary = orderWorkflowType ? isAncillaryWorkflow : isAncillaryByCategory;

      if (isAncillary) {
        const response = await dispatch(getPatient({ patientId: selectedIVR.patientId.toString() }));
        navigate("/ancillaryorder", { state: { ivrId: selectedIVR.ivrId, practiceId: selectedIVR.practiceId, practiceName: selectedIVR.practiceName, patientId: selectedIVR.patientId, providerId: selectedIVR.providerId, providerName: selectedIVR.providerName, orderType: ORDERTYPE.DIRECT_ORDER, patientData: response.payload.data } });
      } else {
        navigate("/order", { state: { fromUI: "IVRList", orderType: ORDERTYPE.DIRECT_ORDER, orderDetails: selectedIVR } });
      }
    }
  }

  const setPatientNavigationPopup = () => {
    setCreateIvrPopup({
      message: 'The "Create IVR" function is moved to the new "Patients" section. From the "Patients" section, search for the patient or add a new patient, and then from the action menu, choose an option to "Create IVR".',
      show: true,
      title: "Create IVR"
    });
  };

  const onCreateClick = async () => {
    if (practiceByUserRoleCreateIVRSlice && practiceByUserRoleCreateIVRSlice.length === 1) {
      const response = await dispatch(fetchPracticeProductsData(practiceByUserRoleCreateIVRSlice[0].practiceId));
      const isExecuted = response.payload.data.some((product: any) =>
        product.agreementStatus === 'EXECUTED' ||
        product.agreementStatus === 'INITIATED' ||
        product.agreementStatus === 'PRACTICE SIGNED'
      );
      if (!isExecuted) {
        setShowInfoPopup({ message: "Could not find any valid Product Agreements. Please try again later.", show: true });
      } else {
        // setIvrSelectionPractice(parseInt(practiceByUserRoleCreateIVRSlice[0].practiceId));
        // setShowIVRSelectionPopup(true);
        setPatientNavigationPopup();
      }
    }
    else if (practiceByUserRoleCreateIVRSlice && practiceByUserRoleCreateIVRSlice.length > 1) {
      // setShowIVRSelectionPopup(true);
      setPatientNavigationPopup();
    } else {
      setShowInfoPopup({ message: "Could not find any valid practice(s) associated with your account. Please try again later.", show: true })
    }
  }

  const onClickInfoPopup = () => {
    setShowInfoPopup({ message: "Could not find any valid Product Agreements. Please try again later.", show: false })
  }

  const onClickCopyIvrPopup = (row: IVRInfo) => {
    navigate("/ivr", { state: { ivrId: row.ivrId, practiceId: row.practiceId, copiedIvr: true } })
  }

  const isOlderThan30Days = (dateString: string) => {
    const givenDate = new Date(dateString);
    const currentDate = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(currentDate.getDate() - 30);

    return givenDate < thirtyDaysAgo;
  };

  const handleCopyIvr = (row: IVRInfo) => {
    if (isOlderThan30Days(row.dateCreated.toString())) {
      setShowIvrCopyMessage({ message: "Information on this IVR is 30 days or older. Please verify the information on this IVR.", show: true, row: row })
    } else {
      navigate("/ivr", { state: { ivrId: row.ivrId, practiceId: row.practiceId, copiedIvr: true } })
    }
  }
  // CS-382 
  const handleDateRangeChange = (fromDate: DateObject | null, toDate: DateObject | null) => {
    const fromDateString = fromDate ? fromDate.format("MM/DD/YYYY") : null;
    const toDateString = toDate ? toDate.format("MM/DD/YYYY") : null;
    setValue("fromDate", fromDateString || "");
    setValue("toDate", toDateString || "");

    setIsDateRangeChanged(true);
  };
  //

  //CS-518 Passing the IVR details to audit log
  const handleClickIVRAudit = (data: any) => {
    if (data && data.ivrNumber) {
      const IVRAuditData = {
        entityName: "IVR",
        entityNumber: data?.ivrNumber
      }

      const url = `/auditlog?entityName=${IVRAuditData.entityName}&entityNumber=${IVRAuditData.entityNumber}`;
      window.open(url, '_blank');
      // navigate("/auditlog", '_blank',{ state: { entityName: IVRAuditData.entityName, entityNumber: IVRAuditData.entityNumber } });
    }
  }

  const handleMenuClose = () => {
    setAnchorElMenu(null);
  };
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenu(event.currentTarget);
  };

  const handleExport = (exportType: string) => {
    const searchCriteriaFromStorage = getSearchCriteriaFromSessionStorage(
      CAN_ACCESS_APPLICATION.IVR_MANAGEMENT +
      "SearchCriteria" +
      userInfo.userId
    );

    if (searchCriteriaFromStorage) {
      let dataToSearch = {
        ...searchCriteria,
        currentPage: searchCriteriaFromStorage.currentPage,
        downloadState: true,
      };

      if (!dataToSearch.practiceId || dataToSearch.practiceId.length === 0) {
        dataToSearch.practiceId = practiceByUserRoleCreateIVRSlice && practiceByUserRoleCreateIVRSlice.length > 0
          ? practiceByUserRoleCreateIVRSlice?.map(practice => Number(practice.practiceId)) : [];
      }
      if (!dataToSearch.productId || dataToSearch.productId.length === 0) {
        dataToSearch.productId = filteredProductsByUserRoleCreateIVRSlice && filteredProductsByUserRoleCreateIVRSlice.length > 0
          ? filteredProductsByUserRoleCreateIVRSlice?.map(product => Number(product.productId)) : [];
      }
      //CS-1625
      if (!dataToSearch.mfgIds || dataToSearch.mfgIds.length === 0) {
        dataToSearch.mfgIds = manufacturesList && manufacturesList.length > 0
          ? manufacturesList?.map(mfg => Number(mfg.value)) : [];
      }
      //
      if (!dataToSearch.verificationStatus || dataToSearch.verificationStatus.length === 0) {
        dataToSearch.verificationStatus = statusOptions && statusOptions.length > 0 ? statusOptions?.map(status => status.value) : [];
      }

      ExportToExcel(
        dataToSearch,
        columns,
        dispatch,
        exportType === "list" ? EXPORTABLE_ITEMS.IVR_LIST : EXPORTABLE_ITEMS.IVR
      );

    }
  };
  //CS-1071 -Start
  const handleActionButtonClick = (buttonId: string, originalClick: () => void) => {
    const currentPageKey = "ivrList";
    saveSelectionForPage(currentPageKey, buttonId)
    setActiveButtonId(buttonId);
    sessionStorage.setItem("activeActionButtonId", buttonId);
    originalClick(); // call the button's actual logic
  };
  //

  // CS-1555 - Start
  const handleRowNavigateToDetails = (rowObj: Row) => {
    // const ivrRow = row as IVRInfo;
    const { ivrStatus, ivrNumberLink, ...row } = rowObj;
    handleActionButtonClick(`ivrListActionButton-${row.ivrId}`, () =>
      navigate("/ivr_details", { state: { ivrInfo: row } })
    );
  };
  // CS-1555 - End

  useEffect(() => {
    if (fetchPracticeAndProductsCompleted) {
      if (userInfo.notificationEnable && canAccessNotifications) {
        if (fetchNotificationCountCompleted && fetchNotificationsCompleted) {
          setLoadingComplete(true)
        }
      } else {
        setLoadingComplete(true)
      }
    }

  }, [fetchNotificationCountCompleted, fetchNotificationsCompleted, fetchPracticeAndProductsCompleted])

  const actionComponent = (rowObj: IVRInfo): JSX.Element => {
    const { ivrStatus, ivrNumberLink, ...row } = rowObj;
    return (
      <>

        <CustomIconButton
          color="info"
          size="small"
          variant="outlined"
          //CS-1071 -Start
          onClick={() => handleActionButtonClick(`ivrListActionButton-${row.ivrId}`, () => navigate("/ivr_details", { state: { ivrInfo: row } }))}
          //CS-1071 -End
          tooltipMessage="Detail"
          tooltipPlacement="top"
        >
          <ListAltOutlined fontSize="small" />
        </CustomIconButton>

        <CustomIconButton
          tooltipPlacement="top"
          tooltipMessage="Create Order"
          color="success"
          size="small"
          variant="outlined"
          style={{ display: `${hideElement(!canCreateDirectOrder || row.status !== IVR_STATUS.VERIFIED)}` }}
          //CS-1071 -Start
          onClick={() => handleActionButtonClick(`ivrListActionButton-${row.ivrId}`, () => handleCreateOrder(row))}
        //CS-1071 -End
        >
          <AppRegistrationOutlined fontSize="small" />
        </CustomIconButton>
        {/* <CustomIconButton
          tooltipPlacement="top"
          tooltipMessage="PDF"
          color="info"
          size="small"
          variant="outlined"
          style={{ display: `${hideElement(row.ivrDocumentId === 0)}` }}
          onClick={() => handlePreviewAction(row)}
        >
          <PictureAsPdfOutlined fontSize="small" />
        </CustomIconButton> */}
        <CustomIconButton
          tooltipPlacement="top"
          tooltipMessage="Edit"
          color="info"
          size="small"
          variant="outlined"
          style={{ display: `${hideElement(!canEditIVR || ![IVR_STATUS.DRAFTED].includes(row.status))}` }}
          //CS-1071 -Start
          onClick={async () => handleActionButtonClick(`ivrListActionButton-${row.ivrId}`, () => {
            navigate("/ivr", {
              state: {
                ivrId: row.ivrId, practiceId: row.practiceId, ivrStatus: row.status, ivrNumberLink: row.ivrNumber,
                patientData: { from: CAN_ACCESS_APPLICATION.IVR_MANAGEMENT, patientId: row.patientId }
              }
            })
          })}
        //CS-1071 -End
        >
          <ModeOutlined fontSize="small" />
        </CustomIconButton>

        <CustomIconButton
          tooltipPlacement="top"
          tooltipMessage="Respond"
          color="warning"
          size="small"
          variant="outlined"
          style={{ display: `${hideElement(!canRespondIVR || (row.status !== IVR_STATUS.SUBMITTED && row.status !== IVR_STATUS.NEEDS_MORE_INFO && row.status !== IVR_STATUS.PRE_AUTH))}` }}
          //CS-1071 -Start
          onClick={() => handleActionButtonClick(`ivrListActionButton-${row.ivrId}`, () => navigate("/benefit_summary", { state: { orderDetails: row } }))}
        //CS-1071 -End
        >
          <SummarizeOutlined fontSize="small" />
        </CustomIconButton>
        {/*<CustomIconButton
          tooltipPlacement="top"
          tooltipMessage="Note"
          color="info"
          size="small"
          variant="outlined"
          style={{ display: `${hideElement(!canNoteIVR)}` }}
          onClick={() => navigate("/ivr_note", { state: { ivrInfo: row } })}
        >
          <EditNote fontSize="small" />
        </CustomIconButton>*/}
        <CustomIconButton
          tooltipPlacement="top"
          tooltipMessage="View"
          color="info"
          size="small"
          variant="outlined"
          style={{ display: `${hideElement(!canViewIVR || ![IVR_STATUS.SUBMITTED, IVR_STATUS.DENIED, IVR_STATUS.NEEDS_MORE_INFO, IVR_STATUS.PRE_AUTH, IVR_STATUS.VERIFIED, IVR_STATUS.PRE_AUTH].includes(row.status))}` }}
          //CS-1071 -Start
          onClick={async () => handleActionButtonClick(`ivrListActionButton-${row.ivrId}`, () => {
            navigate("/ivr", {
              state: {
                ivrId: row.ivrId, viewOnly: true, practiceId: row.practiceId,
                patientData: { from: CAN_ACCESS_APPLICATION.IVR_MANAGEMENT, patientId: row.patientId }
              }
            })
          })}
        //CS-1071 -End
        >
          <ViewInArOutlined fontSize="small" />
        </CustomIconButton>
        {/* <CustomIconButton
          tooltipPlacement="top"
          tooltipMessage="Copy IVR" 
          color="info"
          size="small"
          variant="outlined"
          style={{ display: `${hideElement(!canCopyIVR || [IVR_STATUS.DRAFTED].includes(row.status))}` }}
          onClick={async () => { handleCopyIvr(row) }}
        >  <ContentCopyOutlined fontSize="small" />
        </CustomIconButton> */}
        <CustomIconButton
          tooltipPlacement="top"
          tooltipMessage="Copy IVR"
          color="info"
          size="small"
          variant="outlined"
          style={{ display: `${hideElement(!canCopyIVR || [IVR_STATUS.DRAFTED].includes(row.status))}` }}
          //CS-1071 -Start
          onClick={async () => handleActionButtonClick(`ivrListActionButton-${row.ivrId}`, () => {
            navigate("/ivr", {
              state: {
                ivrId: row.ivrId, practiceId: row.practiceId, duplicateIVR: true,
                patientData: { from: CAN_ACCESS_APPLICATION.IVR_MANAGEMENT, patientId: row.patientId }
              }
            })
          })}
        //CS-1071 -End
        ><ContentCopyOutlined fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          tooltipPlacement="top"
          tooltipMessage="Cancel"
          color="error"
          size="small"
          variant="outlined"
          style={{ display: `${hideElement(!canCancelIVR || [IVR_STATUS.DRAFTED, IVR_STATUS.CANCELED].includes(row.status))}` }}
          //CS-1071 -Start
          onClick={() => handleActionButtonClick(`ivrListActionButton-${row.ivrId}`, () => { setCancelConfirm({ message: "Are you sure you want to cancel this IVR?", show: true, row: row.ivrId }); setSelectedRow(row) })}
        //CS-1071 -End
        >
          <RemoveShoppingCartOutlined fontSize="small" />
        </CustomIconButton>
        <CustomIconButton
          tooltipPlacement="top"
          tooltipMessage="Delete"
          color="error"
          size="small"
          variant="outlined"
          style={{ display: `${hideElement(!canDeleteIVR || row.status !== IVR_STATUS.DRAFTED)}` }}
          //CS-1071 -Start
          onClick={() => handleActionButtonClick(`ivrListActionButton-${row.ivrId}`, () => { setDeleteConfirm({ message: "Are you sure you want to delete this IVR?", show: true, row: row.ivrId }); setSelectedRow(row) })}
        //CS-1071 -End
        >
          <DeleteOutlineOutlined fontSize="small" />
        </CustomIconButton>
        {/* CS-518 */}
        <CustomIconButton
          tooltipPlacement="top"
          tooltipMessage="IVR Audit"
          color="warning"
          size="small"
          variant="outlined"
          style={{ display: `${hideElement(!canAuditIVR)}` }}
          //CS-1071 -Start
          onClick={() => handleActionButtonClick(`ivrListActionButton-${row.ivrId}`, () => handleClickIVRAudit(row))}
        //CS-1071 -End
        >
          <TrackChanges fontSize="small" />
        </CustomIconButton>
      </>
    );
  };

  const legacyItems = [
    { icon: <ModeOutlined color="info" />, text: "Edit" },
    { icon: <DeleteOutlineOutlined color="error" />, text: "Delete" },
    { icon: <VisibilityOutlined color="info" />, text: "Preview" },
    { icon: <AppRegistrationOutlined color="success" />, text: "Create Order" },
    { icon: <ListAltOutlined color="info" />, text: "Detail" },
    { icon: <SummarizeOutlined color="warning" />, text: "Respond" },
    { icon: <EditNote color="info" />, text: "Note" },
    { icon: <ViewInArOutlined color="info" />, text: "View" },
  ];

  // Define columns and rows data
  const columns: Column[] = [
    // { id: "ivrNumber", label: "IVR ID", minWidth: 100 },
    { id: "ivrNumberLink", label: "IVR ID", minWidth: 100 },//CS-1406 - rendering ivr number with link
    { id: "practice", label: "Practice", minWidth: 100 },
    { id: "patient", label: "Patient", minWidth: 100 },
    // { id: "productCategory", label: "Category", minWidth: 100 }, // CS-1326
    { id: "product", label: "Product", minWidth: 100 },
    { id: "manufacturer", label: "Manufacturer", minWidth: 100 },
    { id: "provider", label: "Provider", minWidth: 100 },
    { id: "dateOfService", label: "Date Of Service", minWidth: 80 },
    { id: "ivrStatus", label: "Status", minWidth: 100 },
    // { id: "status", label: "Status", minWidth: 100 },
    { id: "createdDate", label: "Created", minWidth: 100 },
    // { id: "dateCreated", label: "Last Updated Time", minWidth: 80, align: "center" },
    { id: "action", label: "Action", minWidth: 50, },
  ];

  const totalRecords = IVRListInfo?.totalElements ?? 0;
  return (
    <>
      {showIVRSelectionPopup &&
        <IVRPracticeSelectionPopup
          open={showIVRSelectionPopup}
          onClose={() => {
            setShowIVRSelectionPopup(false);
          }}
          practiceByUserRole={practiceByUserRoleCreateIVRSlice}
          practiceId={ivrSelectionPractice}
          patientData={{ from: CAN_ACCESS_APPLICATION.IVR_MANAGEMENT, patientId: null }}
        />
      }
      {showPDF.isShow &&
        <PDFViewer
          isOpen={showPDF.isShow}
          onClose={() => setShowPDF({ isShow: false, productName: "", fileName: "" })}
          title={showPDF.productName}
          fileName={showPDF.fileName}
          base64String={ivrDocumentInfo?.content || ""}
        />
      }
      <CreateIVRModal open={createIVROpen} onClose={() => setCreateIVROpen(false)} />

      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          px: { xs: 1, md: 3 },
          py: 2,
        }}
      >
        {/* -- Filter panel -- */}
        <Box sx={{ position: "relative", zIndex: 20, overflow: "visible" }}>
          <Collapse in={showCard} ref={filterPanelRef} onEntering={handleFilterPanelEntering} onEntered={handleFilterPanelEntered} onExited={handleFilterPanelExited}>
            <Card sx={filterPanelSx}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2} alignItems="flex-end">

                  <Grid item xs={12} sm={3}>
                    <CustomTextField controllerName="ivrNumber" label="IVR ID" placeholder="Enter IVR ID" control={control} size="small" />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <CustomMultipleDropDown name="practiceId" label="Practice" placeholderText="Select practice"
                      options={practiceOptions} control={control} disabled={practiceOptions?.length === 1} size="small" />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <CustomMultipleDropDown name="productCategory" label="Product Category" placeholderText="Select category"
                      options={productCategoryOptions} control={control} disabled={productCategoryOptions?.length === 1} size="small" />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <CustomMultipleDropDown label="Manufacturer" name="mfgIds" placeholderText="Select manufacturer"
                      options={manufacturesList} control={control} size="small" />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <CustomMultipleDropDown name="productId" label="Product" placeholderText="Select product"
                      options={productOptions} control={control} disabled={productOptions?.length === 1} size="small" />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <CustomDateRange
                      label="IVR Created Date Range"
                      control={control}
                      controllerName="dateRange"
                      onChange={handleDateRangeChange}
                      dateRangeMonths={process.env.REACT_APP_REST_SERVICE_URL?.includes('demo') ? undefined : 6}
                      onClear={isClearDateRange}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <CustomTextField controllerName="patientFirstName" label="Patient First Name" placeholder="Enter first name" control={control} size="small" />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <CustomTextField controllerName="patientLastName" label="Patient Last Name" placeholder="Enter last name" control={control} size="small" />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <CustomMultipleDropDown name="verificationStatus" label="Verification Status" placeholderText="Select verification status"
                      options={statusOptions} control={control} size="small" />
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <Button fullWidth size="large" startIcon={<SearchOutlined />}
                      sx={{ ...primaryActionBtnSx, height: "42px", py: 0 }} type="submit" removeUpperCase>
                      Search
                    </Button>
                  </Grid>

                </Grid>
              </form>
            </Card>
          </Collapse>
        </Box>

        {/* -- Main list card -- */}
        <Box mt={2} sx={{ position: "relative", zIndex: 1 }}>
          <Card sx={{ ...listCardSx, p: { xs: "14px !important", md: "18px !important" } }}>

            <Grid container justifyContent="space-between" alignItems="center" spacing={1.2}>

              <Grid item xs={12} md>
                <Typography variant="h3" fontWeight={800} color="text.primary">IVR List</Typography>
              </Grid>

              {/* Notification badge */}
              {userInfo.notificationEnable && canAccessNotifications && totalNotificationIds && totalNotificationIds.length > 0 && (
                <Grid item xs={12} md="auto">
                  <CustomIconButton
                    color="warning"
                    variant="contained"
                    tooltipMessage="IVR Notifications"
                    size="small"
                    notifyCount={totalNotificationIds?.length}
                    onClick={handleNotification}
                  >
                    {isAlertOnly ? <Notifications /> : <NotificationsOutlined />}
                  </CustomIconButton>
                </Grid>
              )}

              {/* Show / Hide filters */}
              <Grid item xs={12} md="auto">
                <Button variant="outlined" color="info" onClick={toggleFilterPanel}
                  startIcon={<SearchOutlined />} fullWidth sx={outlineBtnSx} removeUpperCase>
                  {showCard ? "Hide Filters" : "Show Filters"}
                </Button>
              </Grid>

              {/* Submit IVR */}
              {canCreateIVR && (
                <Grid item xs={12} md="auto">
                  <Button color="info" startIcon={<AssignmentTurnedInOutlined />}
                    onClick={() => setCreateIVROpen(true)} fullWidth sx={primaryActionBtnSx} removeUpperCase>
                    Submit IVR
                  </Button>
                </Grid>
              )}

              {/* Export */}
              {canExportIVRList && (
                <Grid item xs={12} md="auto">
                  {!isMobile && (
                    <Button
                      id="customized-button"
                      aria-controls={Boolean(anchorElMenu) ? "customized-menu" : undefined}
                      aria-haspopup="true"
                      aria-expanded={Boolean(anchorElMenu) ? "true" : undefined}
                      variant="outlined"
                      disableElevation
                      onClick={handleMenuClick}
                      startIcon={<ExitToApp />}
                      sx={exportBtnSx}
                      removeUpperCase
                      endIcon={
                        <Box
                          component="span"
                          sx={{
                            display: "flex",
                            transition: "transform 0.3s ease",
                            transform: anchorElMenu ? "rotate(180deg)" : "rotate(0deg)",
                            padding: "2px",
                          }}
                        >
                          <KeyboardArrowDown />
                        </Box>
                      }
                    >
                      Export
                    </Button>
                  )}

                  {isMobile && (
                    <Button color="success" variant="text" onClick={handleMenuClick}
                      startIcon={<DownloadOutlined fontSize="small" />} sx={exportBtnSx} removeUpperCase>
                      Export
                    </Button>
                  )}

                  <StyledMenu
                    id="customized-menu"
                    MenuListProps={{ "aria-labelledby": "customized-button" }}
                    anchorEl={anchorElMenu}
                    open={Boolean(anchorElMenu)}
                    onClose={handleMenuClose}
                    slotProps={{
                      paper: {
                        sx: {
                          width: "260px",
                          maxWidth: "90vw",
                          mt: 0.75,
                          p: 0.75,
                          borderRadius: "12px",
                          border: `1px solid ${isDark ? "rgba(0,184,217,0.3)" : "rgba(0,184,217,0.22)"}`,
                          backgroundColor: isDark ? "#141720" : "#ffffff",
                          backgroundImage: "none",
                          boxShadow: isDark
                            ? "0 18px 42px rgba(0,0,0,0.45)"
                            : "0 14px 32px rgba(15,23,42,0.14)",
                        },
                      },
                    }}
                  >
                    <Box sx={{ px: 1, mb: 1 }}>
                      <Button fullWidth variant="outlined" startIcon={<CloudDownload />}
                        onClick={() => setShowPHIWarning({
                          show: true,
                          title: "PHI Warning",
                          message: (
                            <center>This export may contain Protected Health Information (PHI). Once exported, data security is your responsibility. Proceed only if you are authorized and will handle this data in compliance with HIPAA.</center>
                          ),
                          variant: "warning",
                          buttons: [
                            { name: "CANCEL", color: "error", onClick: () => setShowPHIWarning({ show: false, message: '' }) },
                            { name: "PROCEED", color: "success", onClick: () => { handleExport("list"); setShowPHIWarning({ show: false, message: '' }); } }
                          ],
                        })}
                        disabled={totalRecords > 1000}
                        sx={exportMenuItemBtnSx}
                        removeUpperCase
                      >
                        Export Search List
                      </Button>
                    </Box>

                    <Box sx={{ px: 1, mb: 1 }}>
                      <Button fullWidth variant="outlined" startIcon={<CloudDownload />}
                        onClick={() => setShowPHIWarning({
                          show: true,
                          title: "PHI Warning",
                          message: (
                            <center>This export may contain Protected Health Information (PHI). Once exported, data security is your responsibility. Proceed only if you are authorized and will handle this data in compliance with HIPAA.</center>
                          ),
                          variant: "warning",
                          buttons: [
                            {
                              name: "CANCEL",
                              color: "error",
                              onClick: () => setShowPHIWarning({ show: false, message: '' })
                            },
                            {
                              name: "PROCEED",
                              color: "success",
                              onClick: () => {
                                handleExport("details");
                                setShowPHIWarning({ show: false, message: '' });
                              }
                            }
                          ],
                        })}
                        disabled={totalRecords > 1000}
                        sx={exportMenuItemBtnSx}
                        removeUpperCase
                      >
                        Export Details List
                      </Button>
                    </Box>

                    {totalRecords > 1000 && (
                      <Box
                        sx={{
                          mx: 1,
                          p: 1.5,
                          mt: 1,
                          backgroundColor: "warning.light",
                          borderRadius: 1,
                          color: "warning.contrastText",
                        }}
                      >
                        <Typography variant="body2">
                          Export is available only when the list contains 1,000 records or fewer.
                          Maximum export size: 1000 records.
                        </Typography>
                      </Box>
                    )}
                  </StyledMenu>
                </Grid>
              )}
            </Grid>

            <Table
              columns={columns}
              rows={loadingComplete ? formattedIVRInfo : []}
              pageNumber={handlePagination}
              totalPages={loadingComplete ? IVRListInfo?.totalPages : 0}
              totalElementText={"Records: " + ((loadingComplete && IVRListInfo?.totalElements) ? IVRListInfo?.totalElements : "0")}
              actionComponent={(row: IVRInfo) => actionComponent(row)}
              emptyText="No IVR(s) Found"
              currentPageNumber={searchCriteria.currentPage - 1}
              showMenu={true}
              sortableColumns={ivrListSort.sortableColumns}
              serverSideSorting
              onSortChange={handleTableSort}
              controlledSortColumn={ivrListSort.controlledColumn(searchCriteria.sortBy)}
              controlledSortOrder={searchCriteria.sortOrder ?? null}
              uniqueValue="ivrId"
              activeButtonId={activeButtonId}
              currentPageKey={"ivrList"}
              // CS-1555 - Start
              onRowClick={handleRowNavigateToDetails}
              clickableCards={true}
              // CS-1555 - End
              sx={ivrTableSx}
            />
          </Card>
        </Box>
      </Box>
      {deleteConfirm.show &&
        <OptionsPopup
          open={deleteConfirm.show}
          onClose={() => setDeleteConfirm({ message: "", show: false, row: "" })}
          variant="confirm"
          message={<>{deleteConfirm.message}</>}
          buttons={[{ name: "Confirm", color: 'primary', onClick: confirmDeleteIVR }, { name: "Cancel", color: 'secondary', onClick: cancelDeleteIVR }]}
        />
      }
      {cancelConfirm.show &&
        <OptionsPopup
          open={cancelConfirm.show}
          onClose={() => setCancelConfirm({ message: "", show: false, row: "" })}
          variant="confirm"
          message={<>{cancelConfirm.message}</>}
          buttons={[{ name: "Confirm", color: 'primary', onClick: confirmCancelIVR }, { name: "Cancel", color: 'secondary', onClick: cancelCancelIVR }]}
        />
      }
      {showInfoPopup.show &&
        <OptionsPopup
          open={showInfoPopup.show}
          onClose={() => setShowInfoPopup({ message: "Could not find any valid Product Agreements. Please try again later.", show: false })}
          variant="info"
          message={<>{showInfoPopup.message}</>}
          buttons={[{ name: "Ok", color: 'primary', onClick: onClickInfoPopup }]}
        />
      }
      {showIvrCopyMessage.show &&
        <OptionsPopup
          open={showIvrCopyMessage.show}
          onClose={() => setShowIvrCopyMessage({ message: "Information on this IVR is 30 days or older. Please verify the information on this IVR.", show: false, row: "" })}
          variant="warning"
          message={<>{showIvrCopyMessage.message}</>}
          buttons={[{ name: "Ok", color: 'primary', onClick: () => onClickCopyIvrPopup(showIvrCopyMessage.row) }]}
        />
      }
      {showCreateIvrPopup.show &&
        <InfoPopup
          open={showCreateIvrPopup.show}
          content={showCreateIvrPopup.message}
          onClose={() => setCreateIvrPopup({ message: "", show: false, title: "" })}
          severity="info"
          confirmButton={"OK"}
          onConfirm={() => { navigate('/patients') }}
          title={showCreateIvrPopup.title}
        />
      }
      {userInfo.notificationEnable &&
        <StatusInfoPopup
          anchorEl={statusAnchorEl}
          onAcknowledge={handleAcknowledge}
          onClose={() => {
            setStatusAnchorEl(null)
          }
          }
          rows={notificationData}
        />
      }
      {/* CS-1700 - Start */}
      {showPHIWarning.show &&
        <WarningPopup
          open={showPHIWarning.show}
          onClose={() => setShowPHIWarning({ show: false, message: '' })}
          title={showPHIWarning.title}
          message={<>{showPHIWarning.message}</>}
          variant={showPHIWarning.variant}
          buttons={showPHIWarning.buttons}
        />
      }
      {/* CS-1700 - End */}
    </>
  )
}

export default IVRList;